import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, STATUS_META, PAYMENT_STATUS_META } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function Orders() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    api.get("/orders").then((r) => setItems(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const customerMap = Object.fromEntries(customers.map((c) => [c.id, c]));

  const filtered = items
    .filter((o) => (filter === "all" ? true : o.status === filter))
    .filter((o) => {
      if (!q) return true;
      const c = customerMap[o.customer_id];
      return (
        o.code?.toLowerCase().includes(q.toLowerCase()) ||
        c?.name?.toLowerCase().includes(q.toLowerCase())
      );
    });

  return (
    <div className="space-y-5" data-testid="orders-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Order Pipeline</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Orders &amp; Quotations</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-44 rounded-sm" data-testid="order-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.keys(STATUS_META).map((k) => (
                <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by code, customer…" className="h-9 pl-8 w-72 rounded-sm" />
          </div>
          {hasRole("admin", "sales") && (
            <Link to="/orders/new">
              <Button className="h-9 rounded-sm bg-black hover:bg-zinc-800 text-white" data-testid="new-order-btn">
                <Plus size={14} className="mr-1" /> New Quotation
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="label-mini px-4 py-3">Order</th>
              <th className="label-mini px-4 py-3">Date</th>
              <th className="label-mini px-4 py-3">Customer</th>
              <th className="label-mini px-4 py-3">Shipping</th>
              <th className="label-mini px-4 py-3">Status</th>
              <th className="label-mini px-4 py-3">Payment</th>
              <th className="label-mini px-4 py-3 text-right">Total (PKR)</th>
              <th className="label-mini px-4 py-3 text-right">Margin (PKR)</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => {
              const c = customerMap[o.customer_id];
              return (
                <tr key={o.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`order-row-${o.code}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <Link to={`/orders/${o.id}`} className="font-semibold underline">{o.code}</Link>
                  </td>
                  <td className="px-4 py-3 text-xs">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3 text-sm">{c?.name || "—"}</td>
                  <td className="px-4 py-3 text-xs capitalize">{(o.shipping_method || "").replace("_", " ")}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3">
                    {(() => {
                      const ps = PAYMENT_STATUS_META[o.payment_status] || PAYMENT_STATUS_META.unpaid;
                      return (
                        <span className="status-badge" style={{ background: ps.bg, color: ps.fg, borderColor: ps.bd }}>
                          {ps.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(o.total_sell || 0, "")}</td>
                  <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(o.margin || 0, "")}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-zinc-500 text-sm">No orders match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
